import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import RHFDatePicker from '@/components/date-picker/RHFDatePicker.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete.tsx';
import { UseFormReturn } from 'react-hook-form';
import { deviceStatusOptions } from '@/routes/landowner/types';
import { DeployDeviceFormSchemaType } from './deploy-device-schema';
import { Checkbox } from '@/components/ui/checkbox';
import { DeployDeviceLoaderResponse } from './types';
import { useLoaderData } from 'react-router';
import { AutoCompleteItem } from '@/components/auto-complete/AutoComplete';

type LandownerFormProps = {
	form: UseFormReturn<DeployDeviceFormSchemaType>;
};

const LandownerStep = (props: LandownerFormProps) => {
	const { form: landownerForm } = props;

	const { sites, landowners } = useLoaderData() as DeployDeviceLoaderResponse;

	const landownerExists = landownerForm.watch('landownerExists');
	const existingLandOwnerId = landownerForm.watch('existingLandownerId')?.[0]?.value;

	const handleLandOwnerSelect = (selected: AutoCompleteItem) => {
		if (selected) {
			const landowner = landowners.data.find(l => l.id.toString() === selected.value);
			if (!landowner || landowner.id.toString() === existingLandOwnerId) {
				landownerForm.resetField('landowner');
				return;
			}
			if (landowner) {
				landownerForm.setValue('landowner', {
					firstName: landowner.firstName,
					lastName: landowner.lastName,
					streetAddress: landowner.streetAddress,
					city: landowner.city,
					state: landowner.state,
					zip: landowner.zip,
					mailingAddress: landowner.mailingAddress ?? '',
					email: landowner.email ?? 'missing@email.com',
					phone: landowner.phone,
					mobile: landowner.mobile ?? '',
					status: landowner.status,
					initialPayment: landowner.initialPayment,
					recurringPayment: landowner.recurringPayment,
					latitude: landowner.latitude,
					longitude: landowner.longitude,
					initialDeploymentDate: new Date(landowner.initialDeploymentDate)
				});
			}
		}
	};

	return (
		<div className="w-full space-y-8">
			<div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 items-start">
				<FormField
					control={landownerForm.control}
					name="landownerExists"
					render={({ field }) => (
						<FormItem className="flex flex-row items-start space-x-3 space-y-0">
							<FormControl>
								<Checkbox
									checked={field.value}
									onCheckedChange={() => {
										field.onChange(!field.value);
										if (field.value) {
											landownerForm.resetField('existingLandownerId');
											landownerForm.resetField('landowner');
										}
									}}
								/>
							</FormControl>
							<div className="space-y-1 leading-none">
								<FormLabel>Selecting an existing landowner</FormLabel>
							</div>
						</FormItem>
					)}
				/>

				<RHFAutoComplete
					name="existingLandownerId"
					label={'Select existing landowner'}
					control={landownerForm.control}
					placeholder="Select a landowner"
					items={landowners.data}
					isDisabled={!landownerExists}
					isMultiSelect={false}
					itemToOption={item => ({
						value: item.id.toString(),
						label: item.firstName + ' ' + item.lastName
					})}
					closeOnSelect
					onSelect={select => handleLandOwnerSelect(select)}
				/>
			</div>
			<div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 items-start">
				<FormField
					control={landownerForm.control}
					name="landowner.firstName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>First Name</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.lastName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Last Name</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.streetAddress"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Street Address</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.city"
					render={({ field }) => (
						<FormItem>
							<FormLabel>City</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.state"
					render={({ field }) => (
						<FormItem>
							<FormLabel>State</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.zip"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Zip</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.mailingAddress"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Mailing Address</FormLabel>
							<FormControl>
								<Input
									type="text"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input
									type="email"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.phone"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Phone</FormLabel>
							<FormControl>
								<Input
									type="tel"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.mobile"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Mobile</FormLabel>
							<FormControl>
								<Input
									type="tel"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<RHFAutoComplete
					name="deploymentSiteId"
					isDisabled
					label={'Site'}
					control={landownerForm.control}
					placeholder="Select a site"
					items={sites.filter(s => s.id !== 0)}
					isMultiSelect={false}
					itemToOption={item => ({
						value: item.id.toString(),
						label: item.name
					})}
					closeOnSelect
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.status"
					render={({ field }) => (
						<FormItem>
							<FormItem>
								<FormLabel>Status</FormLabel>

								<Select
									onValueChange={field.onChange}
									value={field.value ?? ''}
									disabled={landownerExists}
								>
									<FormControl>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Select a status" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{deviceStatusOptions.map(opt => (
											<SelectItem key={opt.value} value={opt.value.toString()}>
												{opt.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.initialPayment"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Initial Payment</FormLabel>
							<FormControl>
								<Input
									type="number"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.recurringPayment"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Recurring Payment</FormLabel>
							<FormControl>
								<Input
									type="number"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.latitude"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Latitude</FormLabel>
							<FormControl>
								<Input
									type="number"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={landownerForm.control}
					name="landowner.longitude"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Longitude</FormLabel>
							<FormControl>
								<Input
									type="number"
									className="input"
									onChange={field.onChange}
									name={field.name}
									value={field.value ?? ''}
									disabled={landownerExists}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<RHFDatePicker
					control={landownerForm.control}
					name="landowner.initialDeploymentDate"
					label="Initial deployment date"
					placeholder="Pick a date"
					isFullWidth={true}
					isDisabledFn={() => landownerExists}
				/>
			</div>
		</div>
	);
};

export default LandownerStep;
